import type { VenueStatus, VenueType, VenueListItem, VenueDetail } from "./types";

/** Re-exported from `shared/lib` — the canonical, locale-aware date formatter. */
export { formatDateShort } from "@/shared/lib/format-date";

/** Translation key for a venue status badge. Copy lives under `venuesDirectory.statusLabel.<STATUS>`. */
export function venueStatusLabelKey(status: VenueStatus): string {
  return `venuesDirectory.statusLabel.${status}`;
}

/** Translation key for a venue type badge. Copy lives under `venuesDirectory.typeLabel.<TYPE>`. */
export function venueTypeLabelKey(type: VenueType): string {
  return `venuesDirectory.typeLabel.${type}`;
}

/** Badge color intent per status — keeps the badge component dumb. */
export function venueStatusVariant(
  status: VenueStatus,
): "default" | "secondary" | "outline" | "success" {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "PENDING_APPROVAL":
    case "CHANGES_REQUESTED":
      return "outline";
    case "SUSPENDED":
    case "REJECTED":
      return "secondary";
    default:
      return "default";
  }
}

/**
 * Single-line address for a list row / detail header. Joins the parts
 * that exist and skips the blanks so we never render "City, , US".
 */
export function formatVenueLocation(
  venue: Pick<VenueListItem | VenueDetail, "address" | "city" | "state" | "country">,
): string {
  return [venue.address, venue.city, venue.state, venue.country]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(", ");
}
